class ProductGallery extends HTMLElement {
  constructor() {
    super();
    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.handleDotClick = this.handleDotClick.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleZoomClick = this.handleZoomClick.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.track = this.querySelector('.product-gallery__track');
    this.medias = Array.from(this.querySelectorAll('.product-gallery__media'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail-id]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot-id]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.zoomButtons = this.querySelectorAll('[data-zoom-trigger]');
    
    this.bindEvents();
    this.updateActiveMedia();
  }

  disconnectedCallback() {
    this.thumbnails.forEach(thumb => {
      thumb.removeEventListener('click', this.handleThumbnailClick);
    });
    this.dots.forEach(dot => {
      dot.removeEventListener('click', this.handleDotClick);
    });
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    this.zoomButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleZoomClick);
    });
    if (this.viewport) {
      this.viewport.removeEventListener('scroll', this.handleScroll);
    }
    clearTimeout(this.scrollTimeout);
  }

  handleThumbnailClick(e) {
    const thumb = e.currentTarget;
    this.setActiveMedia(thumb.dataset.thumbnailId);
  }

  handleDotClick(e) {
    const dot = e.currentTarget;
    this.setActiveMedia(dot.dataset.dotId);
  }

  handlePrevClick() {
    this.navigate(-1);
  }

  handleNextClick() {
    this.navigate(1);
  }

  handleZoomClick(e) {
    const btn = e.currentTarget;
    const modal = document.getElementById('product-zoom-modal-' + this.dataset.sectionId);
    if (modal) {
      modal.open(btn);
      const zoomMedia = modal.querySelector('[data-zoom-media-id="' + btn.dataset.zoomTrigger + '"]');
      if (zoomMedia) {
        zoomMedia.scrollIntoView({ behavior: 'instant' });
      }
    }
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => this.updateActiveFromScroll(), 50);
  }

  bindEvents() {
    this.thumbnails.forEach(thumb => {
      thumb.addEventListener('click', this.handleThumbnailClick);
    });
    this.dots.forEach(dot => {
      dot.addEventListener('click', this.handleDotClick);
    });
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }
    this.zoomButtons.forEach(btn => {
      btn.addEventListener('click', this.handleZoomClick);
    });

    if (this.viewport) {
      this.viewport.addEventListener('scroll', this.handleScroll);
    }
  }

  updateActiveFromScroll() {
    if (!this.viewport || this.medias.length === 0) return;
    const viewportRect = this.viewport.getBoundingClientRect();
    const center = viewportRect.left + viewportRect.width / 2;
    
    let closestMedia = this.medias[0];
    let closestDistance = Infinity;

    this.medias.forEach(media => {
      const rect = media.getBoundingClientRect();
      const mediaCenter = rect.left + rect.width / 2;
      const distance = Math.abs(mediaCenter - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestMedia = media;
      }
    });

    if (closestMedia && this.activeMediaId !== closestMedia.dataset.mediaId) {
      this.updateActiveState(closestMedia.dataset.mediaId, false);
    }
  }

  // Public API
  setActiveMedia(mediaId, scroll = true) {
    this.updateActiveState(mediaId, scroll);
  }

  updateActiveState(mediaId, scroll = true) {
    this.activeMediaId = mediaId;
    const targetMedia = this.medias.find(m => m.dataset.mediaId === mediaId);
    
    this.medias.forEach(media => {
      if (media.dataset.mediaId === mediaId) {
        media.setAttribute('data-active', 'true');
        if (scroll && this.viewport) {
          // Check for reduced motion
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const behavior = prefersReducedMotion ? 'instant' : 'smooth';
          media.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
        }
        this.playMedia(media);
      } else {
        media.removeAttribute('data-active');
        this.pauseMedia(media);
      }
    });

    this.thumbnails.forEach(thumb => {
      if (thumb.dataset.thumbnailId === mediaId) {
        thumb.setAttribute('aria-current', 'true');
        // Check for reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = prefersReducedMotion ? 'instant' : 'smooth';
        if (scroll) {
          thumb.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
        } else {
          // Not a gallery control interaction (page load, the viewport's own scroll): move the thumbnail strip only.
          // scrollIntoView would also scroll the page - on a homepage Featured Product it jumped the window down on load.
          this.revealWithin(thumb.parentElement, thumb, 'center', behavior);
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.dots.forEach(dot => {
      if (dot.dataset.dotId === mediaId) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.updateNavButtons();
  }

  navigate(direction) {
    const currentIndex = this.medias.findIndex(m => m.dataset.mediaId === this.activeMediaId);
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= this.medias.length) newIndex = this.medias.length - 1;
    
    this.setActiveMedia(this.medias[newIndex].dataset.mediaId);
  }

  updateNavButtons() {
    if (!this.prevButton || !this.nextButton) return;
    const currentIndex = this.medias.findIndex(m => m.dataset.mediaId === this.activeMediaId);
    this.prevButton.disabled = currentIndex === 0;
    this.nextButton.disabled = currentIndex === this.medias.length - 1;
  }

  playMedia(media) {
    const video = media.querySelector('video');
    if (video && this.dataset.videoAutoplay === 'true') {
      video.play().catch(() => {});
    }
  }

  pauseMedia(media) {
    const video = media.querySelector('video');
    if (video) {
      video.pause();
    }
  }

  updateActiveMedia() {
    const activeMedia = this.medias.find(m => m.hasAttribute('data-active')) || this.medias[0];
    if (activeMedia) {
      // scroll instantly on load so the selected variant media is visible
      this.updateActiveState(activeMedia.dataset.mediaId, false);
      // Position the gallery's own scrollers only. Initialization must never move the page (window.scrollY).
      if (this.viewport && activeMedia !== this.medias[0]) {
        this.revealWithin(this.viewport, activeMedia, 'start', 'instant');
      }
      
      const activeThumb = this.thumbnails.find(t => t.dataset.thumbnailId === activeMedia.dataset.mediaId);
      if (activeThumb) {
        this.revealWithin(activeThumb.parentElement, activeThumb, 'center', 'instant');
      }
    }
  }

  // Scrolls `container` (and nothing above it) so `element` is in view: horizontally to its start or centre, vertically to
  // the nearest edge. Deltas are physical, so it is correct in RTL too, where scrollLeft runs negative.
  revealWithin(container, element, inline, behavior) {
    if (!container || !element) return;

    const box = container.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    let left = 0;
    let top = 0;

    if (container.scrollWidth > container.clientWidth + 1) {
      if (inline === 'center') {
        left = rect.left + rect.width / 2 - (box.left + box.width / 2);
      } else {
        left = getComputedStyle(container).direction === 'rtl' ? rect.right - box.right : rect.left - box.left;
      }
    }

    if (container.scrollHeight > container.clientHeight + 1) {
      if (rect.top < box.top) top = rect.top - box.top;
      else if (rect.bottom > box.bottom) top = rect.bottom - box.bottom;
    }

    if (Math.abs(left) >= 1 || Math.abs(top) >= 1) container.scrollBy({ left, top, behavior });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}